import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AppUser, CommunityPost, ToastType, UserProfile, UserReportReason, UserRelationship, SocialRelationshipStatus, CosmeticSlot, UserCosmeticLoadout } from '../types';
import { followUser, unfollowUser } from '../services/following';
import {
  acceptFriendRequest,
  blockUser,
  cancelFriendRequest,
  declineFriendRequest,
  removeFriend,
  sendFriendRequest,
  subscribeToRelationshipBetween,
  subscribeToPublicUserProfile,
} from '../services/users';
import { submitUserReport } from '../services/reports';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { RoddoFishIcon } from './icons';
import { getFeaturedBadges, buildReputationSummary } from '../utils/reputation';
import { getCosmeticById, getCosmeticSlotLabel, resolveCosmeticLoadout } from '../constants/cosmetics';

interface PublicProfileViewProps {
  currentUser: UserProfile;
  targetUserId: string;
  users?: Record<string, AppUser>;
  posts?: CommunityPost[];
  followingIds?: string[];
  isDarkMode?: boolean;
  onBack: () => void;
  addToast: (message: string, type?: ToastType) => void;
}

const DEFAULT_AVATAR = '/avatar-default.png';

type PublicStats = {
  catches: number;
  species: number;
  spots: number;
  posts: number;
};

const pickAvatar = (...vals: any[]) => {
  for (const v of vals) {
    const s = typeof v === 'string' ? v.trim() : '';
    if (s) return s;
  }
  return DEFAULT_AVATAR;
};

const cleanString = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const safeNumber = (value: unknown, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const normKey = (value: unknown) => cleanString(value).toLowerCase();

function getPostAuthorId(post: any) {
  return String(
    post?.authorId ||
      post?.userId ||
      post?.uid ||
      post?.ownerId ||
      post?.createdBy ||
      post?.author?.uid ||
      post?.user?.uid ||
      '',
  ).trim();
}

function getFirstString(...values: any[]) {
  for (const value of values) {
    const s = cleanString(value);
    if (s) return s;
  }
  return '';
}

function getEntrySpecies(entry: any) {
  return getFirstString(
    entry?.speciesName,
    entry?.species,
    entry?.fishSpecies,
    entry?.fishType,
    entry?.fishName,
    entry?.commonName,
    entry?.type,
    entry?.fish?.species,
    entry?.fish?.name,
    entry?.catchData?.species,
    entry?.catchData?.fishSpecies,
    entry?.catchData?.fishType,
    entry?.catchData?.fishName,
    entry?.catch?.species,
    entry?.catch?.fishSpecies,
    entry?.catch?.fishType,
    entry?.diaryEntry?.species,
    entry?.diaryEntry?.fishSpecies,
    entry?.diaryEntry?.fishType,
    entry?.metadata?.species,
  );
}

function getEntrySpot(entry: any) {
  return getFirstString(
    entry?.waterName,
    entry?.location,
    entry?.locationName,
    entry?.spotName,
    entry?.waterbody,
    entry?.bodyOfWater,
    entry?.placeName,
    entry?.catchData?.waterName,
    entry?.catchData?.location,
    entry?.catchData?.locationName,
    entry?.catchData?.spotName,
    entry?.catch?.waterName,
    entry?.catch?.location,
    entry?.diaryEntry?.waterName,
    entry?.diaryEntry?.location,
    entry?.metadata?.waterName,
  );
}

function getPostSpecies(post: any) {
  const direct = getEntrySpecies(post);
  if (direct) return direct;

  const text = getFirstString(post?.caption, post?.text, post?.body, post?.content).toLowerCase();
  const knownSpecies = [
    'bass',
    'largemouth bass',
    'smallmouth bass',
    'striped bass',
    'trout',
    'rainbow trout',
    'brown trout',
    'salmon',
    'mackerel',
    'tuna',
    'halibut',
    'catfish',
    'crappie',
    'walleye',
    'pike',
    'carp',
    'bluegill',
    'perch',
    'redfish',
    'snook',
    'tarpon',
    'crab',
  ];

  return knownSpecies.find((name) => text.includes(name)) || '';
}

function getPostSpot(post: any) {
  return getEntrySpot(post);
}

function looksLikeCatchPost(post: any) {
  if (!post) return false;
  if (post?.catchData || post?.catch || post?.diaryEntry || post?.catchDiaryId || post?.catchId || post?.logId) return true;
  if (getPostSpecies(post)) return true;

  const caption = getFirstString(post?.caption, post?.text, post?.body, post?.content).toLowerCase();
  return /\b(caught|catch|landed|released|keeper|lb|lbs|pound|oz|fish|bass|trout|salmon|mackerel|tuna|halibut|catfish|crappie|walleye|pike|carp|bluegill|perch|crab)\b/.test(caption);
}

function getProfileEntries(profile: any) {
  const buckets = [
    profile?.log,
    profile?.catchLog,
    profile?.catchDiary,
    profile?.catchEntries,
    profile?.catches,
    profile?.recentCatches,
    profile?.stats?.catchLog,
    profile?.stats?.catchDiary,
    profile?.stats?.catches,
  ];

  return buckets.flatMap((bucket) => (Array.isArray(bucket) ? bucket : []));
}

function deriveStatsFromEntries(entries: any[]): PublicStats {
  const species = new Set<string>();
  const spots = new Set<string>();

  entries.forEach((entry) => {
    const sp = normKey(getEntrySpecies(entry));
    const spot = normKey(getEntrySpot(entry));
    if (sp) species.add(sp);
    if (spot) spots.add(spot);
  });

  return {
    catches: entries.length,
    species: species.size,
    spots: spots.size,
    posts: 0,
  };
}

function derivePublicStatsFromPosts(targetUserId: string, posts: CommunityPost[]) {
  const mine = (Array.isArray(posts) ? posts : []).filter((post: any) => getPostAuthorId(post) === targetUserId);
  const catchPosts = mine.filter((post: any) => looksLikeCatchPost(post));
  const species = new Set<string>();
  const spots = new Set<string>();

  catchPosts.forEach((post: any) => {
    const sp = normKey(getPostSpecies(post));
    const spot = normKey(getPostSpot(post));
    if (sp) species.add(sp);
    if (spot) spots.add(spot);
  });

  return {
    catches: catchPosts.length,
    species: species.size,
    spots: spots.size,
    posts: mine.length,
  };
}

function hasCanonicalPublicStats(profile: any): boolean {
  return profile?.stats?.source === 'catch_inventory_v2' && Number(profile?.stats?.schemaVersion || 0) >= 2;
}

function deriveStatsFromProfile(profile: any): PublicStats {
  const stats = profile?.stats || {};

  // Once the server has projected the current deduped catch inventory, those
  // numbers are authoritative. Do not let legacy counters or embedded arrays
  // inflate them again.
  if (hasCanonicalPublicStats(profile)) {
    return {
      catches: Math.max(0, safeNumber(stats?.catches ?? stats?.totalCatches)),
      species: Math.max(0, safeNumber(stats?.species ?? stats?.uniqueSpeciesCount)),
      spots: Math.max(0, safeNumber(stats?.waters ?? stats?.spots ?? stats?.uniqueWatersCount)),
      posts: Math.max(0, safeNumber(stats?.posts ?? stats?.communityPosts)),
    };
  }

  const entries = getProfileEntries(profile);
  const entryStats = deriveStatsFromEntries(entries);

  return {
    catches: Math.max(
      safeNumber(profile?.totalCatches),
      safeNumber(profile?.catchCount),
      safeNumber(profile?.catchesCount),
      safeNumber(stats?.catches),
      safeNumber(stats?.totalCatches),
      safeNumber(stats?.catchCount),
      safeNumber(stats?.catchesCount),
      entryStats.catches,
    ),
    species: Math.max(
      safeNumber(profile?.uniqueSpeciesCount),
      safeNumber(profile?.speciesCount),
      safeNumber(profile?.fishSpeciesCount),
      safeNumber(stats?.species),
      safeNumber(stats?.speciesCount),
      safeNumber(stats?.uniqueSpecies),
      safeNumber(stats?.uniqueSpeciesCount),
      safeNumber(stats?.fishSpeciesCount),
      Array.isArray(profile?.species) ? profile.species.length : 0,
      Array.isArray(profile?.uniqueSpecies) ? profile.uniqueSpecies.length : 0,
      Array.isArray(stats?.speciesList) ? stats.speciesList.length : 0,
      Array.isArray(stats?.uniqueSpeciesList) ? stats.uniqueSpeciesList.length : 0,
      entryStats.species,
    ),
    spots: Math.max(
      safeNumber(profile?.uniqueWatersCount),
      safeNumber(profile?.watersCount),
      safeNumber(profile?.spotsCount),
      safeNumber(profile?.locationsCount),
      safeNumber(stats?.spots),
      safeNumber(stats?.locations),
      safeNumber(stats?.waters),
      safeNumber(stats?.uniqueWaters),
      safeNumber(stats?.uniqueWatersCount),
      safeNumber(stats?.spotsCount),
      safeNumber(stats?.locationsCount),
      Array.isArray(profile?.waters) ? profile.waters.length : 0,
      Array.isArray(profile?.locations) ? profile.locations.length : 0,
      Array.isArray(stats?.watersList) ? stats.watersList.length : 0,
      Array.isArray(stats?.locationsList) ? stats.locationsList.length : 0,
      entryStats.spots,
    ),
    posts: Math.max(
      safeNumber(profile?.communityPostsCount),
      safeNumber(profile?.postsCount),
      safeNumber(stats?.communityPosts),
      safeNumber(stats?.posts),
      safeNumber(stats?.postsCount),
    ),
  };
}

function mergeStats(...parts: Array<Partial<PublicStats> | null | undefined>): PublicStats {
  return parts.reduce<PublicStats>(
    (acc, next) => ({
      catches: Math.max(acc.catches, safeNumber(next?.catches)),
      species: Math.max(acc.species, safeNumber(next?.species)),
      spots: Math.max(acc.spots, safeNumber(next?.spots)),
      posts: Math.max(acc.posts, safeNumber(next?.posts)),
    }),
    { catches: 0, species: 0, spots: 0, posts: 0 },
  );
}

function mergePublicProfile(liveProfile: any, selfProfile: any, fallback: any, targetUserId: string) {
  const base = {
    ...(fallback || {}),
    ...(selfProfile || {}),
    ...(liveProfile || {}),
  };

  const liveStats = liveProfile?.stats || {};
  const selfStats = selfProfile?.stats || {};
  const fallbackStats = fallback?.stats || {};
  const mergedStats = mergeStats(deriveStatsFromProfile(fallback), deriveStatsFromProfile(selfProfile), deriveStatsFromProfile(liveProfile));
  // For your own profile, App's live unified timeline is fresher than the
  // public mirror while a server refresh is in flight. Other anglers still use
  // only their public projection.
  const canonicalStatsOwner = hasCanonicalPublicStats(liveProfile)
    ? liveProfile
    : hasCanonicalPublicStats(fallback)
      ? fallback
      : null;
  const canonicalStatsRaw = canonicalStatsOwner?.stats || null;
  const canonicalStats = canonicalStatsOwner ? deriveStatsFromProfile(canonicalStatsOwner) : null;
  const resolvedStats = canonicalStats
    ? { ...canonicalStats, posts: mergedStats.posts }
    : mergedStats;

  return {
    ...base,
    uid: liveProfile?.uid || selfProfile?.uid || fallback?.uid || targetUserId,
    name:
      cleanString(liveProfile?.name) ||
      cleanString(liveProfile?.displayName) ||
      cleanString(selfProfile?.name) ||
      cleanString(selfProfile?.displayName) ||
      cleanString(fallback?.name) ||
      cleanString(fallback?.displayName) ||
      'Angler',
    displayName:
      cleanString(liveProfile?.displayName) ||
      cleanString(liveProfile?.name) ||
      cleanString(selfProfile?.displayName) ||
      cleanString(selfProfile?.name) ||
      cleanString(fallback?.displayName) ||
      cleanString(fallback?.name) ||
      'Angler',
    username: cleanString(liveProfile?.username) || cleanString(selfProfile?.username) || cleanString(fallback?.username),
    avatar: pickAvatar(liveProfile?.avatar, liveProfile?.avatarUrl, liveProfile?.photoURL, selfProfile?.avatar, selfProfile?.avatarUrl, selfProfile?.photoURL, fallback?.avatar, fallback?.avatarUrl, fallback?.photoURL),
    bio: cleanString(liveProfile?.bio) || cleanString(selfProfile?.bio) || cleanString(fallback?.bio),
    location: cleanString(liveProfile?.location) || cleanString(selfProfile?.location) || cleanString(fallback?.location),
    points: Math.max(safeNumber(liveProfile?.points), safeNumber(liveProfile?.xp), safeNumber(selfProfile?.points), safeNumber(selfProfile?.xp), safeNumber(fallback?.points), safeNumber(fallback?.xp)),
    xp: Math.max(safeNumber(liveProfile?.xp), safeNumber(liveProfile?.points), safeNumber(selfProfile?.xp), safeNumber(selfProfile?.points), safeNumber(fallback?.xp), safeNumber(fallback?.points)),
    level: Math.max(1, safeNumber(liveProfile?.level, 1), safeNumber(selfProfile?.level, 1), safeNumber(fallback?.level, 1)),
    achievements: Array.isArray(liveProfile?.achievements) ? liveProfile.achievements : Array.isArray(selfProfile?.achievements) ? selfProfile.achievements : Array.isArray(fallback?.achievements) ? fallback.achievements : [],
    cosmeticLoadout: (liveProfile?.cosmeticLoadout || selfProfile?.cosmeticLoadout || fallback?.cosmeticLoadout || null) as UserCosmeticLoadout | null,
    cosmeticLoadoutUpdatedAt: liveProfile?.cosmeticLoadoutUpdatedAt || selfProfile?.cosmeticLoadoutUpdatedAt || fallback?.cosmeticLoadoutUpdatedAt || null,
    stats: {
      ...fallbackStats,
      ...selfStats,
      ...liveStats,
      ...(canonicalStatsRaw || {}),
      catches: resolvedStats.catches,
      totalCatches: resolvedStats.catches,
      species: resolvedStats.species,
      speciesCount: resolvedStats.species,
      uniqueSpecies: resolvedStats.species,
      uniqueSpeciesCount: resolvedStats.species,
      spots: resolvedStats.spots,
      waters: resolvedStats.spots,
      locations: resolvedStats.spots,
      uniqueWaters: resolvedStats.spots,
      uniqueWatersCount: resolvedStats.spots,
      posts: resolvedStats.posts,
      postsCount: Math.max(resolvedStats.posts, safeNumber(liveStats?.postsCount), safeNumber(selfStats?.postsCount), safeNumber(fallbackStats?.postsCount)),
      communityPosts: Math.max(resolvedStats.posts, safeNumber(liveStats?.communityPosts), safeNumber(selfStats?.communityPosts), safeNumber(fallbackStats?.communityPosts)),
    },
    onboardingCompleted: true,
    following: Array.isArray(base?.following) ? base.following : [],
  } as UserProfile;
}

function buildFallbackReputation(profile: any) {
  return buildReputationSummary({
    species_mastery: safeNumber(profile?.stats?.catches),
    explorer: safeNumber(profile?.stats?.spots),
    community_contributor: safeNumber(profile?.stats?.communityPosts ?? profile?.stats?.posts),
    bazaar_trader: safeNumber(profile?.stats?.bazaarSales ?? profile?.stats?.trades),
    tournament_competitor: safeNumber(profile?.stats?.tournaments),
  });
}

export default function PublicProfileView(props: PublicProfileViewProps) {
  const { t } = useTranslation();
  const { currentUser, targetUserId, users = {}, posts = [], followingIds = [], isDarkMode, onBack, addToast } = props;
  const isDark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(isDark) as any;

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFollowBusy, setIsFollowBusy] = useState(false);
  const [relationship, setRelationship] = useState<UserRelationship | null>(null);
  const [relationshipState, setRelationshipState] = useState<SocialRelationshipStatus>('none');
  const [isCrewBusy, setIsCrewBusy] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState<UserReportReason>('spam');
  const [reportDetails, setReportDetails] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  useEffect(() => {
    setLoading(true);
    const unsub = subscribeToPublicUserProfile(
      targetUserId,
      (next) => {
        setProfile(next);
        setLoading(false);
      },
      (err) => {
        console.error('[PublicProfileView] subscribe failed', err);
        setLoading(false);
      },
    );
    return () => unsub?.();
  }, [targetUserId]);

  const recentPosts = useMemo(
    () =>
      (Array.isArray(posts) ? posts : [])
        .filter((p: any) => getPostAuthorId(p) === targetUserId)
        .slice()
        .sort((a: any, b: any) => Number(b?.timestamp || b?.createdAt || 0) - Number(a?.timestamp || a?.createdAt || 0))
        .slice(0, 3),
    [posts, targetUserId],
  );

  const totalPublicPosts = useMemo(
    () => (Array.isArray(posts) ? posts : []).filter((p: any) => getPostAuthorId(p) === targetUserId).length,
    [posts, targetUserId],
  );

  const publicStats = useMemo(() => derivePublicStatsFromPosts(targetUserId, posts), [posts, targetUserId]);

  const mergedProfile = useMemo(() => {
    const fallback = users[targetUserId];
    const selfProfile = currentUser?.uid === targetUserId ? currentUser : null;
    if (!profile && !selfProfile && !fallback) return null;
    return mergePublicProfile(profile, selfProfile, fallback, targetUserId);
  }, [currentUser, profile, targetUserId, users]);

  const isFollowing = followingIds.includes(targetUserId);

  useEffect(() => {
    const currentUid = String((currentUser as any)?.uid || '').trim();
    const targetUid = String(targetUserId || '').trim();
    if (!currentUid || !targetUid || currentUid === targetUid) {
      setRelationship(null);
      setRelationshipState('none');
      return;
    }
    return subscribeToRelationshipBetween(currentUid, targetUid, (rel, state) => {
      setRelationship(rel);
      setRelationshipState(state);
    }, (err) => console.warn('[PublicProfileView] relationship subscription failed', err));
  }, [currentUser, targetUserId]);
  const canShowStats = mergedProfile?.publicProfileVisibility?.showStats !== false;
  const canShowAbout = mergedProfile?.publicProfileVisibility?.showAbout !== false;
  const canShowAchievements = mergedProfile?.publicProfileVisibility?.showAchievements !== false;

  const reputation = (mergedProfile as any)?.reputation || buildFallbackReputation(mergedProfile);
  const badges = getFeaturedBadges(reputation);

  const level = Math.max(1, safeNumber((mergedProfile as any)?.level, 1));
  const xp = safeNumber((mergedProfile as any)?.xp ?? (mergedProfile as any)?.points, 0);

  const equippedCosmetics = useMemo(() => {
    const resolved = resolveCosmeticLoadout((mergedProfile as any)?.cosmeticLoadout);
    return Object.values(resolved)
      .map((id) => getCosmeticById(id))
      .filter(Boolean) as NonNullable<ReturnType<typeof getCosmeticById>>[];
  }, [(mergedProfile as any)?.cosmeticLoadout]);

  const equippedFrame = equippedCosmetics.find((item) => item.slot === 'profileFrame');
  const publicAvatarGear = equippedCosmetics.filter((item) => item.slot !== 'profileFrame').slice(0, 5);

  const handleCrewAction = async () => {
    const currentUid = String((currentUser as any)?.uid || '').trim();
    const targetUid = String(targetUserId || '').trim();
    if (!currentUid || !targetUid || currentUid === targetUid || isCrewBusy) return;
    try {
      setIsCrewBusy(true);
      if (relationshipState === 'pending_sent') {
        await cancelFriendRequest(currentUid, targetUid);
        addToast(t('publicProfile.crewCanceled', 'Crew request canceled.'), 'success');
      } else if (relationshipState === 'pending_received') {
        await acceptFriendRequest(currentUid, targetUid);
        addToast(t('publicProfile.crewAccepted', 'Crew request accepted.'), 'success');
      } else if (relationshipState === 'friends') {
        addToast(t('publicProfile.alreadyCrew', 'Already in your crew. Use the menu to remove.'), 'info');
      } else if (relationshipState === 'blocked_by_me') {
        addToast(t('publicProfile.blockedAngler', 'You blocked this angler.'), 'info');
      } else if (relationshipState === 'blocked_me' || relationshipState === 'blocked') {
        addToast(t('publicProfile.crewUnavailable', 'Crew is unavailable for this angler.'), 'error');
      } else {
        await sendFriendRequest(currentUid, targetUid);
        addToast(t('publicProfile.crewSent', 'Crew request sent.'), 'success');
      }
    } catch (err) {
      console.error('[PublicProfileView] crew action failed', err);
      addToast(t('publicProfile.crewFailed', 'Crew action failed.'), 'error');
    } finally {
      setIsCrewBusy(false);
    }
  };

  const handleToggleFollow = async () => {
    if (!currentUser?.uid || !targetUserId || currentUser.uid === targetUserId || isFollowBusy) return;
    try {
      setIsFollowBusy(true);
      if (isFollowing) {
        await unfollowUser(currentUser.uid, targetUserId);
        addToast(t('publicProfile.unfollowed', 'Unfollowed.'), 'success');
      } else {
        await followUser(currentUser.uid, targetUserId);
        addToast(t('publicProfile.followingToast', 'Following!'), 'success');
      }
    } catch (err) {
      console.error('[PublicProfileView] follow toggle failed', err);
      addToast(t('publicProfile.followFailed', 'Could not update follow state.'), 'error');
    } finally {
      setIsFollowBusy(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!mergedProfile) return;
    try {
      setIsSubmittingReport(true);
      await submitUserReport({
        reporterUid: currentUser.uid,
        reporterName: currentUser.name,
        targetUserId: mergedProfile.uid,
        targetUserName: mergedProfile.name,
        reason: reportReason,
        details: reportDetails,
      });
      setIsReportOpen(false);
      setReportDetails('');
      setReportReason('spam');
      addToast(t('publicProfile.reportSubmitted', 'Report submitted.'), 'success');
    } catch (err: any) {
      console.error('[PublicProfileView] report failed', err);
      addToast(err?.message || t('publicProfile.reportFailed', 'Could not submit report.'), 'error');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="rounded-[14px] p-4" style={{ background: ui.cardBg, border: `1px solid ${ui.border}` }}>
          {t('publicProfile.loading', 'Loading profile…')}
        </div>
      </div>
    );
  }

  if (!mergedProfile) {
    return (
      <div className="p-4">
        <div className="rounded-[14px] p-4" style={{ background: ui.cardBg, border: `1px solid ${ui.border}` }}>
          {t('publicProfile.notFound', 'User not found.')}
        </div>
      </div>
    );
  }

  const avatar = pickAvatar((mergedProfile as any).avatar, (mergedProfile as any).avatarUrl, (mergedProfile as any).photoURL, users[targetUserId]?.avatar);
  const achievements = Array.isArray(mergedProfile.achievements) ? mergedProfile.achievements : [];
  const profileStats = deriveStatsFromProfile(mergedProfile as any);
  const stats = hasCanonicalPublicStats(mergedProfile)
    ? { ...profileStats, posts: Math.max(profileStats.posts, publicStats.posts) }
    : mergeStats(profileStats, publicStats);
  const memberNumber = (mergedProfile as any).memberNumber;
  const displayName = String(mergedProfile.name || (mergedProfile as any).displayName || t('profile.angler', 'Angler')).trim() || t('profile.angler', 'Angler');
  const username = String((mergedProfile as any).username || '').trim();
  const displayNameSize =
    displayName.length >= 20 ? '19px' :
    displayName.length >= 16 ? '21px' :
    displayName.length >= 12 ? '24px' :
    displayName.length >= 10 ? '28px' :
    '32px';

  const profileVars = {
    '--public-panel-light': ui.cardBgStrong ?? ui.cardBg,
    '--public-panel-soft': ui.cardBgMuted ?? ui.surfaceBgSoft ?? ui.cardBg,
    '--public-text': ui.textPrimary,
    '--public-muted': ui.textSecondary,
    '--public-border': isDark ? 'rgba(159,140,220,0.16)' : 'rgba(116,86,54,0.22)',
  } as React.CSSProperties;

  const glassCard =
    'relative isolate overflow-hidden rounded-[16px] border p-4 sm:p-5 ' +
    'shadow-[0_6px_16px_rgba(89,65,37,0.06),inset_0_1px_0_rgba(255,255,255,0.50)] ' +
    
    'dark:shadow-[0_6px_16px_rgba(0,0,0,0.16),inset_0_1px_0_rgba(255,255,255,0.03)]';

  const softCard =
    'relative overflow-hidden rounded-[16px] border p-4 shadow-[0_5px_14px_rgba(89,65,37,0.06),inset_0_1px_0_rgba(255,255,255,0.44)] ' +
    'dark:shadow-[0_5px_14px_rgba(0,0,0,0.14),inset_0_1px_0_rgba(255,255,255,0.03)]';

  const chipClass = 'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-extrabold backdrop-blur-xl';
  const eyebrowClass = 'text-[10px] font-black uppercase tracking-[0.22em]';

  return (
    <div
      className={`${isDark ? 'dark' : ''} px-3 py-3 sm:px-4 sm:py-4`}
      data-roddo-theme={isDark ? 'dark' : 'light'}
      style={{ ...profileVars, color: ui.textPrimary, background: ui.pageBg ?? 'transparent' }}
    >
      <div className="mx-auto max-w-4xl space-y-3.5">
        <div
          className={glassCard}
          style={{
            background: isDark
              ? '#101827'
              : 'linear-gradient(180deg, rgba(255,248,226,0.98) 0%, rgba(255,238,196,0.98) 100%)',
            borderColor: ui.borderStrong ?? ui.border,
          }}
        >
          <div className="relative z-[1] space-y-5">
            <div className="flex items-center justify-between gap-3">
              <div className={eyebrowClass} style={{ color: ui.textSecondary }}>{t('publicProfile.passport', 'Angler passport')}</div>
              <button
                type="button"
                onClick={onBack}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full border text-xl font-black leading-none transition hover:-translate-y-0.5 active:translate-y-px"
                style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border, color: ui.textPrimary }}
                aria-label={t('common.back', 'Back')}
                title={t('common.back', 'Back')}
              >
                ‹
              </button>
            </div>

            <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
              <div className="shrink-0 sm:w-28">
                <div
                  className="relative h-24 w-24 rounded-[14px] p-1"
                  style={{
                    background: equippedFrame
                      ? 'linear-gradient(135deg, rgba(255,215,106,0.98), rgba(124,240,196,0.88), rgba(121,199,255,0.92))'
                      : 'rgba(255,215,106,0.92)',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.14)',
                  }}
                  title={equippedFrame ? `${equippedFrame.name} equipped` : undefined}
                >
                  <img
                    src={avatar}
                    alt={displayName}
                    className="h-full w-full rounded-[16px] object-cover"
                  />
                  {equippedFrame ? (
                    <div className="absolute -right-2 -top-2 grid h-8 w-8 place-items-center rounded-full border border-white bg-[#fff7d6] text-base shadow-[0_4px_12px_rgba(0,0,0,0.10)]">
                      {equippedFrame.emoji}
                    </div>
                  ) : null}
                </div>
                {username ? (
                  <div
                    className="mt-2 max-w-[112px] truncate rounded-full border px-2.5 py-1 text-center text-[11px] font-black"
                    style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border, color: ui.textSecondary }}
                    title={`@${username}`}
                  >
                    @{username}
                  </div>
                ) : null}
              </div>

              <div className="min-w-0 flex-1">
                <div className="min-w-0">
                  <h1
                    className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-extrabold leading-none tracking-[-0.045em] sm:text-[36px]"
                    style={{ color: ui.textPrimary, fontSize: displayNameSize }}
                    title={displayName}
                  >
                    {displayName}
                  </h1>
                </div>

                {currentUser.uid !== targetUserId ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={isCrewBusy}
                      onClick={handleCrewAction}
                      aria-label={t('publicProfile.crewAction', 'Crew action')}
                      title={t('publicProfile.crewAction', 'Crew action')}
                      className="inline-flex h-9 items-center justify-center rounded-2xl px-3 text-[12px] font-black transition hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
                      style={{ background: '#7cf0c4', color: '#143226', border: '1px solid rgba(20,50,38,0.18)', boxShadow: '0 5px 14px rgba(20,50,38,0.10)' }}
                    >
                      {relationshipState === 'friends' ? t('publicProfile.crew', 'Crew ✓') : relationshipState === 'pending_sent' ? t('publicProfile.requested', 'Requested') : relationshipState === 'pending_received' ? t('common.accept', 'Accept') : relationshipState === 'blocked_by_me' ? t('publicProfile.blocked', 'Blocked') : t('publicProfile.addCrew', 'Add Crew')}
                    </button>

                    <button
                      type="button"
                      onClick={() => setIsReportOpen(true)}
                      aria-label={t('publicProfile.reportUser', 'Report user')}
                      title={t('publicProfile.report', 'Report')}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-2xl text-xs font-black transition hover:-translate-y-0.5 active:scale-95"
                      style={{ background: '#ffb4b4', color: '#5e1d1d', border: '1px solid rgba(94,29,29,0.16)', boxShadow: '0 5px 14px rgba(94,29,29,0.10)' }}
                    >
                      ⚑
                    </button>
                  </div>
                ) : null}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className={chipClass} style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border, color: ui.textPrimary }}>🌊 {t('profile.level', 'Level')} {level}</span>
                  <span className={chipClass} style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border, color: ui.textPrimary }}>✨ {xp.toLocaleString()} XP</span>
                  {typeof memberNumber === 'number' ? (
                    <span className={chipClass} style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border, color: ui.textPrimary }}>{t('profile.memberNumber', 'Member #{{number}}', { number: memberNumber })}</span>
                  ) : null}
                  {((mergedProfile as any)?.foundingMember?.eligible === true || (typeof memberNumber === 'number' && memberNumber > 0 && memberNumber <= 5000)) ? (
                    <span className={chipClass} style={{ background: '#FFF2BE', borderColor: 'rgba(116,86,54,0.28)', color: '#3A2F17' }}>⚓ Founding Member</span>
                  ) : null}
                </div>

                {equippedFrame || publicAvatarGear.length ? (
                  <div className="mt-3 rounded-[14px] border p-3" style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border }}>
                    <div className={eyebrowClass} style={{ color: ui.textSecondary }}>{t('publicProfile.currentOutfit', 'Current avatar outfit')}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {equippedFrame ? (
                        <span className={chipClass} style={{ background: ui.surfaceBg ?? ui.cardBg, borderColor: ui.border, color: ui.textPrimary }} title={`${getCosmeticSlotLabel(equippedFrame.slot as CosmeticSlot)}: ${equippedFrame.name}`}>
                          {equippedFrame.emoji} {equippedFrame.name}
                        </span>
                      ) : null}
                      {publicAvatarGear.map((item) => (
                        <span key={item.id} className={chipClass} style={{ background: ui.surfaceBg ?? ui.cardBg, borderColor: ui.border, color: ui.textPrimary }} title={`${getCosmeticSlotLabel(item.slot as CosmeticSlot)}: ${item.name}`}>
                          {item.emoji} {item.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {badges.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {badges.map((badge) => (
                      <span key={badge.key} className={chipClass} style={{ background: ui.surfaceBg ?? ui.cardBg, borderColor: ui.border, color: ui.textPrimary }}>
                        {badge.icon} {badge.label}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {canShowStats ? (
          <div className={softCard} style={{ background: ui.cardBg, borderColor: ui.border }}>
            <div className="relative z-[1] mb-3 flex items-center justify-between gap-2">
              <div className={eyebrowClass} style={{ color: ui.textSecondary }}>{t('publicProfile.scorecard', 'Angler scorecard')}</div>
              <div className="rounded-full border px-3 py-1 text-[11px] font-black" style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border, color: ui.textPrimary }}>
                {t('profile.level', 'Level')} {level}
              </div>
            </div>

            <div className="relative z-[1] space-y-2">
              <div className="flex items-center gap-3 rounded-[14px] border px-3 py-2.5 shadow-[0_4px_12px_rgba(89,65,37,0.06)]"
                style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border }}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#7CF0C4] to-[#3ABF77] text-lg text-white shadow-[0_5px_0_rgba(15,107,73,0.18)]">
                  🌿
                </div>
                <div className="min-w-0 flex-1">
                  <div className={eyebrowClass} style={{ color: ui.textSecondary }}>{t('publicProfile.rankTitle', 'Rank title')}</div>
                  <div className="truncate text-[16px] font-black" style={{ color: ui.textPrimary }}>
                    {badges[0]?.label || `${t('profile.level', 'Level')} ${level} ${t('profile.angler', 'Angler')}`}
                  </div>
                </div>
                <div className="rounded-full border px-3 py-1 text-[10px] font-black" style={{ background: '#7CF0C4', borderColor: 'rgba(15,107,73,0.18)', color: '#0F5137' }}>
                  LV {level}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[
                  { label: t('publicProfile.catchLog', 'Catch log'), value: safeNumber(stats.catches), meta: t('publicProfile.logged', 'Logged'), icon: '🎣', bg: 'linear-gradient(135deg,#79C7FF,#2479B8)', color: isDark ? '#9DDAFF' : '#0E6EA8' },
                  { label: t('publicProfile.speciesCodex', 'Species codex'), value: safeNumber(stats.species), meta: t('publicProfile.learned', 'Learned'), icon: '🐟', bg: 'linear-gradient(135deg,#7CF0C4,#3ABF77)', color: isDark ? '#A7F3D0' : '#0F6B49' },
                  { label: t('publicProfile.waterSpots', 'Water spots'), value: safeNumber(stats.spots), meta: t('publicProfile.mapped', 'Mapped'), icon: '📍', bg: 'linear-gradient(135deg,#FFE08A,#F59E0B)', color: isDark ? '#FFE8A3' : '#8B6400' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 rounded-[14px] border px-3 py-2.5 shadow-[0_4px_12px_rgba(89,65,37,0.06)]"
                    style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border }}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] text-lg text-white shadow-[0_4px_10px_rgba(0,0,0,0.10)]" style={{ background: item.bg }}>
                      {item.icon === '🐟' ? (
                        <RoddoFishIcon mode="png" animated={false} className="h-6 w-7 object-contain" />
                      ) : (
                        item.icon
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className={eyebrowClass} style={{ color: ui.textSecondary }}>{item.label}</div>
                      <div className="text-[20px] font-black tabular-nums leading-none" style={{ color: item.color }}>
                        {Number(item.value).toLocaleString()}
                      </div>
                      <div className="mt-0.5 text-[10px] font-bold" style={{ color: ui.textSecondary }}>{item.meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {canShowAbout ? (
          <div className={softCard} style={{ background: ui.cardBg, borderColor: ui.border }}>
            <div className="relative z-[1]">
              <div className={eyebrowClass} style={{ color: ui.textSecondary }}>{t('publicProfile.about', 'About this angler')}</div>
              <p className="mt-2 whitespace-pre-line text-sm leading-6" style={{ color: ui.textPrimary }}>
                {String(mergedProfile.bio || '').trim() || t('publicProfile.noDockNote', 'This angler has not written their dock note yet.')}
              </p>
            </div>
          </div>
        ) : null}

        {canShowAchievements && achievements.length > 0 ? (
          <div className={softCard} style={{ background: ui.cardBg, borderColor: ui.border }}>
            <div className="relative z-[1] flex items-center justify-between gap-2">
              <div className={eyebrowClass} style={{ color: ui.textSecondary }}>{t('publicProfile.fieldMarks', 'Field marks')}</div>
              <div className="text-xs font-bold" style={{ color: ui.textSecondary }}>{t('publicProfile.shownCount', '{{count}} shown', { count: achievements.length })}</div>
            </div>
            <div className="relative z-[1] mt-3 flex flex-wrap gap-2">
              {achievements.slice(0, 24).map((item, index) => (
                <div key={`${item.name}-${index}`} className="rounded-full border px-3 py-2 text-sm font-bold" style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border }}>
                  <span className="mr-2">{item.icon || '🏆'}</span>
                  <span>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className={softCard} style={{ background: ui.cardBg, borderColor: ui.border }}>
          <div className="relative z-[1] flex items-center justify-between gap-2">
            <div className={eyebrowClass} style={{ color: ui.textSecondary }}>{t('publicProfile.recentMoments', 'Recent water moments')}</div>
            <div className="text-xs font-bold" style={{ color: ui.textSecondary }}>{t('publicProfile.shownOfTotal', '{{shown}} of {{total}} shown', { shown: recentPosts.length, total: totalPublicPosts })}</div>
          </div>
          {recentPosts.length === 0 ? (
            <p className="relative z-[1] mt-2 text-sm" style={{ color: ui.textSecondary }}>{t('publicProfile.noMoments', 'No water moments yet.')}</p>
          ) : (
            <div className="relative z-[1] mt-3 space-y-3">
              {recentPosts.map((post: any) => {
                const body = String(post?.caption ?? post?.text ?? post?.body ?? '').trim();
                const image = Array.isArray(post?.images) && post.images[0] ? post.images[0] : post?.imageUrl || post?.image || null;
                const species = getPostSpecies(post);
                const spot = getPostSpot(post);
                return (
                  <div key={String(post.id)} className="relative overflow-hidden rounded-[14px] border p-3 shadow-[0_5px_14px_rgba(89,65,37,0.07)]" style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border }}>
                    <div className="text-[11px] font-bold" style={{ color: ui.textSecondary }}>
                      {post?.timestamp ? new Date(post.timestamp).toLocaleString() : post?.createdAt ? new Date(post.createdAt).toLocaleString() : ''}
                    </div>
                    {(species || spot) ? <div className="mt-1 text-sm font-bold">{[species, spot].filter(Boolean).join(' @ ')}</div> : null}
                    {body ? <div className="mt-1 line-clamp-2 whitespace-pre-line text-sm" style={{ color: ui.textSecondary }}>{body}</div> : null}
                    {image ? <img src={image} alt={t('publicProfile.postImageAlt', 'post')} className="mt-3 max-h-[190px] w-full rounded-[16px] object-cover shadow-[0_10px_24px_rgba(0,0,0,0.12)]" /> : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isReportOpen ? (
        <div className="fixed inset-0 z-[99999] grid place-items-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-[28px] p-4 sm:p-5" style={{ background: ui.cardBgStrong ?? ui.cardBg, border: `2px solid ${ui.borderStrong ?? ui.border}` }}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-lg font-extrabold">{t('publicProfile.reportUser', 'Report user')}</div>
              <button type="button" onClick={() => setIsReportOpen(false)} className="rounded-full px-3 py-1.5 text-sm font-extrabold" style={{ background: ui.surfaceBg ?? ui.cardBg, border: `1px solid ${ui.border}` }}>
                ✕
              </button>
            </div>
            <div className="mt-3 space-y-3">
              <label className="block">
                <div className="mb-1 text-xs font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>{t('publicProfile.reason', 'Reason')}</div>
                <select value={reportReason} onChange={(e) => setReportReason(e.target.value as UserReportReason)} className="w-full rounded-[14px] px-3 py-2 text-sm" style={{ background: ui.surfaceBg ?? ui.cardBg, border: `1px solid ${ui.border}` }}>
                  <option value="spam">{t('publicProfile.reasonSpam', 'Spam')}</option>
                  <option value="harassment">{t('publicProfile.reasonHarassment', 'Harassment')}</option>
                  <option value="impersonation">{t('publicProfile.reasonImpersonation', 'Impersonation')}</option>
                  <option value="inappropriate_content">{t('publicProfile.reasonInappropriate', 'Inappropriate content')}</option>
                  <option value="scam">{t('publicProfile.reasonScam', 'Scam')}</option>
                  <option value="other">{t('publicProfile.reasonOther', 'Other')}</option>
                </select>
              </label>
              <label className="block">
                <div className="mb-1 text-xs font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>{t('publicProfile.details', 'Details')}</div>
                <textarea rows={4} value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} className="w-full rounded-[14px] px-3 py-2 text-sm" style={{ background: ui.surfaceBg ?? ui.cardBg, border: `1px solid ${ui.border}` }} placeholder={t('publicProfile.detailsPlaceholder', 'Optional details for moderators')} />
              </label>
              <button type="button" disabled={isSubmittingReport} onClick={handleSubmitReport} className="w-full rounded-[14px] px-4 py-3 text-sm font-extrabold" style={{ background: 'linear-gradient(180deg, #ffd7d7 0%, #ff9f9f 100%)', color: '#5e1d1d' }}>
                {isSubmittingReport ? t('publicProfile.submitting', 'Submitting…') : t('publicProfile.submitReport', 'Submit report')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
